/*!
 * Copyright © 2019-2020. Verizon Connect Ireland Limited. All rights reserved.
 */

import { ListRange } from '@angular/cdk/collections';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { FlatTreeControl } from '@angular/cdk/tree';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs';
import { ECheckboxState } from '../../../models/checkbox-state.enum';
import { FlatTreeDataSource, FlatTreeVirtualScrollingDataSource, IFlatTreeNode } from '../shared';
import { ScopingTreeSelectionModel } from './scoping-tree-selection-model';

@Component({
  selector: 'fm-scoping-tree',
  templateUrl: './scoping-tree.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ScopingTreeComponent implements OnChanges, OnDestroy, AfterViewInit {
  @Input()
  treeData: IFlatTreeNode<string>[];

  @Output()
  selectionChange = new EventEmitter<IFlatTreeNode<string>[]>();

  @ViewChild(CdkVirtualScrollViewport)
  virtualScroll: CdkVirtualScrollViewport;

  checklistSelection: ScopingTreeSelectionModel<string>;
  dataSource: FlatTreeDataSource<IFlatTreeNode<string>>;
  expandedNodesDataSource: FlatTreeVirtualScrollingDataSource<IFlatTreeNode<string>>;
  treeControl: FlatTreeControl<IFlatTreeNode<string>>;
  readonly treeNodeSize = 26;

  ECheckboxState = ECheckboxState;

  private _selectionChangeSubscription: Subscription;
  private readonly _virtualScrollingRangeChange$: Observable<ListRange>;
  private readonly _virtualScrollingRangeChangeSubject = new Subject<ListRange>();
  private _virtualScrollingRenderedRangeSubscription: Subscription;

  constructor() {
    this._virtualScrollingRangeChange$ = this._virtualScrollingRangeChangeSubject.asObservable();
  }

  ngAfterViewInit(): void {
    this._virtualScrollingRenderedRangeSubscription = this.virtualScroll.renderedRangeStream.subscribe(range => {
      this._virtualScrollingRangeChangeSubject.next(range);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['treeData']) {
      this._handleTreeDataReceived();
    }
  }

  ngOnDestroy(): void {
    if (this._selectionChangeSubscription) {
      this._selectionChangeSubscription.unsubscribe();
    }
    if (this._virtualScrollingRenderedRangeSubscription) {
      this._virtualScrollingRenderedRangeSubscription.unsubscribe();
    }
  }

  getLevel = (node: IFlatTreeNode<string>) => node.level;
  hasChild = (_: number, node: IFlatTreeNode<string>) => node.isExpandable;
  isExpandable = (node: IFlatTreeNode<string>) => node.isExpandable;

  toggleSelection(node: IFlatTreeNode<string>): void {
    this.checklistSelection.toggle(node);
  }

  private _handleTreeDataReceived(): void {
    this.treeData = this.treeData.map(node => ({ ...node }));
    const range: ListRange = { start: 0, end: 50 };
    if (this.virtualScroll) {
      const viewportSize = this.virtualScroll.getViewportSize();
      range.end = Math.round(viewportSize / this.treeNodeSize + 10);
      this.virtualScroll.setRenderedRange(range);
      this.virtualScroll.scrollToIndex(0);
    }

    this.treeControl = new FlatTreeControl<IFlatTreeNode<string>>(this.getLevel, this.isExpandable);
    this.dataSource = new FlatTreeDataSource(this.treeControl, this.treeData, this._virtualScrollingRangeChange$, range);
    this.expandedNodesDataSource = new FlatTreeVirtualScrollingDataSource(this.treeControl);
    this.checklistSelection = new ScopingTreeSelectionModel(this.treeControl);

    this._initializeExpansion();
    this._setupSelectionChange();

    this._emitInitialSelection();
  }

  private _initializeExpansion(): void {
    if (this.treeData.length > 0) {
      this.treeControl.expand(this.treeData[0]);
    }
  }

  private _setupSelectionChange(): void {
    this._selectionChangeSubscription = this.checklistSelection.selectionChange$.subscribe(selectedNodes =>
      this.selectionChange.emit(selectedNodes)
    );
  }

  private _emitInitialSelection(): void {
    const selectedNodes = this.checklistSelection.getSelectedNodes();
    this.selectionChange.emit(selectedNodes);
  }
}
